import React, { useState, useEffect } from 'react';
import { getGuest } from '../../../services/getApi';
import { getRoomPrices } from '../../../services/roomPriceApi';

// تنظیمات پیش‌فرض
const defaultSettings = {
  theme: 'dark',          // ← اکنون یکی از تم‌های DaisyUI
  fontSize: 'medium',
  fontFamily: 'default',
  primaryColor: 'primary',
  borderRadius: 'medium',
  compactMode: false,
  animations: true,
  language: 'fa',
  dateFormat: 'jalaali',
  currencyFormat: 'toman',
  notifications: true,
  autoSave: true,
};

// اندازه فونت‌ها
const fontSizes = {
  small: { name: 'کوچک', value: '14px', class: 'text-sm' },
  medium: { name: 'متوسط', value: '16px', class: 'text-base' },
  large: { name: 'بزرگ', value: '18px', class: 'text-lg' },
  xlarge: { name: 'خیلی بزرگ', value: '20px', class: 'text-xl' }
};

// رنگ‌های اصلی (برای استایل‌های سفارشی)
const primaryColors = [
  { name: 'آبی', value: 'primary', color: '#3b82f6' },
  { name: 'سبز', value: 'success', color: '#22c55e' },
  { name: 'قرمز', value: 'error', color: '#ef4444' },
  { name: 'نارنجی', value: 'warning', color: '#f59e0b' },
  { name: 'بنفش', value: 'purple', color: '#8b5cf6' },
  { name: 'فیروزه‌ای', value: 'cyan', color: '#06b6d4' },
  { name: 'صورتی', value: 'pink', color: '#ec4899' },
];

// اندازه حاشیه
const borderRadii = {
  small: { name: 'کوچک', value: '4px' },
  medium: { name: 'متوسط', value: '8px' },
  large: { name: 'بزرگ', value: '16px' },
  full: { name: 'دایره‌ای', value: '9999px' }
};

// لیست تم‌های DaisyUI (نام انگلیسی به همراه نام نمایشی فارسی ساده)
const daisyuiThemes = [
  { value: 'light', label: 'روشن (Light)' },
  { value: 'dark', label: 'تاریک (Dark)' },
  { value: 'cupcake', label: 'کاپ‌کیک (Cupcake)' },
  { value: 'bumblebee', label: 'بامبل‌بی (Bumblebee)' },
  { value: 'emerald', label: 'زمرد (Emerald)' },
  { value: 'corporate', label: 'شرکتی (Corporate)' },
  { value: 'synthwave', label: 'سینت‌ویو (Synthwave)' },
  { value: 'retro', label: 'رترو (Retro)' },
  { value: 'cyberpunk', label: 'سایبرپانک (Cyberpunk)' },
  { value: 'valentine', label: 'ولنتاین (Valentine)' },
  { value: 'halloween', label: 'هالووین (Halloween)' },
  { value: 'garden', label: 'باغ (Garden)' },
  { value: 'forest', label: 'جنگل (Forest)' },
  { value: 'aqua', label: 'آبی (Aqua)' },
  { value: 'lofi', label: 'لوفی (Lofi)' },
  { value: 'pastel', label: 'پاستل (Pastel)' },
  { value: 'fantasy', label: 'فانتزی (Fantasy)' },
  { value: 'wireframe', label: 'وایرفریم (Wireframe)' },
  { value: 'black', label: 'مشکی (Black)' },
  { value: 'luxury', label: 'لوکس (Luxury)' },
  { value: 'dracula', label: 'دراکولا (Dracula)' },
  { value: 'cmyk', label: 'CMYK' },
  { value: 'autumn', label: 'پاییز (Autumn)' },
  { value: 'business', label: 'بیزینس (Business)' },
  { value: 'acid', label: 'اسید (Acid)' },
  { value: 'lemonade', label: 'لیموناد (Lemonade)' },
  { value: 'night', label: 'شب (Night)' },
  { value: 'coffee', label: 'قهوه (Coffee)' },
  { value: 'winter', label: 'زمستان (Winter)' },
];

function Setting() {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('appSettings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  const [activeTab, setActiveTab] = useState('appearance');
  const [saved, setSaved] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  // اعمال تنظیمات روی DOM
  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  const applySettings = (newSettings) => {
    const root = document.documentElement;
    
    // ========== تم DaisyUI ==========
    // فقط data-theme را تنظیم می‌کنیم (کلاس‌های light/dark روی body حذف می‌شوند)
    root.setAttribute('data-theme', newSettings.theme);
    
    // (اختیاری) اگر کلاس‌های قبلی light/dark روی body وجود داشت، پاک می‌کنیم
    document.body.classList.remove('light', 'dark');
    
    // ========== سایر تنظیمات ظاهری ==========
    // اعمال اندازه فونت
    root.style.setProperty('--app-font-size', fontSizes[newSettings.fontSize]?.value || '16px');
    
    // اعمال رنگ اصلی (برای المان‌های سفارشی)
    root.style.setProperty('--app-primary-color', primaryColors.find(c => c.value === newSettings.primaryColor)?.color || '#3b82f6');
    
    // اعمال حاشیه
    root.style.setProperty('--app-border-radius', borderRadii[newSettings.borderRadius]?.value || '8px');
    
    // اعمال انیمیشن
    if (!newSettings.animations) {
      root.classList.add('no-animations');
    } else {
      root.classList.remove('no-animations');
    }
    
    // اعمال حالت فشرده
    if (newSettings.compactMode) {
      root.classList.add('compact-mode');
    } else {
      root.classList.remove('compact-mode');
    }
  };

  const saveSettings = () => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const resetSettings = () => {
    if (window.confirm('آیا از بازنشانی تنظیمات به حالت پیش‌فرض اطمینان دارید؟')) {
      setSettings(defaultSettings);
      localStorage.removeItem('appSettings');
    }
  };

  // پاک کردن کش
  const clearCache = () => {
    setClearing(true);
    setTimeout(() => {
      localStorage.removeItem('guestsCache');
      localStorage.removeItem('roomPricesCache');
      localStorage.removeItem('reportsCache');
      setClearing(false);
      alert('کش با موفقیت پاک شد');
    }, 1000);
  };

  // خروجی داده‌ها
  const exportData = async () => {
    setExporting(true);
    try {
      const [guestsRes, roomPricesRes] = await Promise.all([
        getGuest().catch(() => ({ data: [] })),
        getRoomPrices().catch(() => ({ data: [] }))
      ]);
      
      const exportObj = {
        exportDate: new Date().toISOString(),
        settings: settings,
        guests: guestsRes.data || [],
        roomPrices: roomPricesRes.data || []
      };
      
      const dataStr = JSON.stringify(exportObj, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `hotel-backup-${new Date().toLocaleDateString('fa-IR').replace(/\//g, '-')}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } catch (err) {
      console.error('Export error:', err);
      alert('خطا در خروجی گرفتن داده‌ها');
    } finally {
      setExporting(false);
    }
  };

  // ورود داده‌ها
  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportError('');
    setImportSuccess('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        if (importedData.settings) {
          setSettings(importedData.settings);
          localStorage.setItem('appSettings', JSON.stringify(importedData.settings));
        }
        
        if (importedData.guests && importedData.guests.length > 0) {
          localStorage.setItem('importedGuests', JSON.stringify(importedData.guests));
        }
        
        if (importedData.roomPrices && importedData.roomPrices.length > 0) {
          localStorage.setItem('importedRoomPrices', JSON.stringify(importedData.roomPrices));
        }
        
        setImportSuccess(`${importedData.guests?.length || 0} مهمان و ${importedData.roomPrices?.length || 0} قیمت اتاق با موفقیت وارد شد`);
      } catch (err) {
        setImportError('فرمت فایل نامعتبر است');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-4 page-ani">
      {/* Header ... (بدون تغییر) */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.056 1.676-1.056 2.096 0l1.07 2.632c.15.366.476.6.823.672l2.833.555c1.094.214 1.526 1.45.966 2.3l-1.823 2.453c-.26.35-.356.79-.26 1.21l.6 2.833c.214 1.094-.823 1.956-1.823 1.45l-2.533-1.333c-.366-.2-.8-.2-1.166 0l-2.533 1.333c-1 .5-2.037-.356-1.823-1.45l.6-2.833c.096-.42 0-.86-.26-1.21l-1.823-2.453c-.56-.85-.128-2.086.966-2.3l2.833-.555c.347-.072.673-.306.823-.672l1.07-2.632z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
          </svg>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">تنظیمات</h1>
          <p className="text-sm text-gray-400">شخصی‌سازی ظاهر و رفتار برنامه</p>
        </div>
      </div>

      {/* Tabs (بدون تغییر) */}
      <div className="tabs tabs-boxed mb-6 bg-base-300/50 p-1">
        <button 
          className={`tab gap-2 ${activeTab === 'appearance' ? 'tab-active bg-primary text-white' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          ظاهر
        </button>
        <button 
          className={`tab gap-2 ${activeTab === 'general' ? 'tab-active bg-primary text-white' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          عمومی
        </button>
        <button 
          className={`tab gap-2 ${activeTab === 'data' ? 'tab-active bg-primary text-white' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          داده‌ها
        </button>
      </div>

      {/* Content */}
      <div className="bg-base-300/50 rounded-xl p-6 shadow-xl">
        {activeTab === 'appearance' && (
          <div className="space-y-6">
            {/* ========== تم برنامه (جایگزین حالت نمایش) ========== */}
            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">تم برنامه</p>
                  <p className="text-sm text-gray-400">انتخاب پوسته رنگی دلخواه (DaisyUI)</p>
                </div>
              </div>
              <div className="w-48">
                <select
                  className="select select-bordered w-full bg-base-100"
                  value={settings.theme}
                  onChange={(e) => updateSetting('theme', e.target.value)}
                >
                  {daisyuiThemes.map((theme) => (
                    <option key={theme.value} value={theme.value}>
                      {theme.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* اندازه فونت (بدون تغییر) */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">اندازه فونت</p>
                  <p className="text-sm text-gray-400">تنظیم اندازه متن در برنامه</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(fontSizes).map(([key, value]) => (
                  <button
                    key={key}
                    className={`btn ${settings.fontSize === key ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => updateSetting('fontSize', key)}
                  >
                    {value.name}
                  </button>
                ))}
              </div>
            </div>

            {/* رنگ اصلی (بدون تغییر) */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">رنگ اصلی</p>
                  <p className="text-sm text-gray-400">انتخاب رنگ اصلی برنامه</p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                {primaryColors.map((color) => (
                  <button
                    key={color.value}
                    className={`w-10 h-10 rounded-lg transition-transform hover:scale-110 ${settings.primaryColor === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-base-300' : ''}`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => updateSetting('primaryColor', color.value)}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* گردی حاشیه (بدون تغییر) */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">گردی حاشیه</p>
                  <p className="text-sm text-gray-400">میزان گردی گوشه‌های المان‌ها</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(borderRadii).map(([key, value]) => (
                  <button
                    key={key}
                    className={`btn ${settings.borderRadius === key ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => updateSetting('borderRadius', key)}
                  >
                    {value.name}
                  </button>
                ))}
              </div>
            </div>

            {/* انیمیشن‌ها (بدون تغییر) */}
            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">انیمیشن‌ها</p>
                  <p className="text-sm text-gray-400">فعال/غیرفعال کردن انیمیشن‌ها</p>
                </div>
              </div>
              <input 
                type="checkbox" 
                className="toggle toggle-primary" 
                checked={settings.animations}
                onChange={(e) => updateSetting('animations', e.target.checked)}
              />
            </div>

            {/* حالت فشرده (بدون تغییر) */}
            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">حالت فشرده</p>
                  <p className="text-sm text-gray-400">نمایش فشرده‌تر المان‌ها</p>
                </div>
              </div>
              <input 
                type="checkbox" 
                className="toggle toggle-primary" 
                checked={settings.compactMode}
                onChange={(e) => updateSetting('compactMode', e.target.checked)}
              />
            </div>
          </div>
        )}

        {activeTab === 'general' && (
          /* ... همان کد قبلی بدون تغییر ... */
          <div className="space-y-6">
            {/* Language */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.922 18.922 0 016.365 9.023m8.834 4.585L15 15m-6-6l-2 2m2-2l2 2m-2-2V9a2 2 0 012-2h2m0 0V7a2 2 0 012-2h2m0 0V5a2 2 0 012-2h2" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">زبان</p>
                  <p className="text-sm text-gray-400">انتخاب زبان برنامه</p>
                </div>
              </div>
              <select 
                className="select select-bordered w-full bg-base-100"
                value={settings.language}
                onChange={(e) => updateSetting('language', e.target.value)}
              >
                <option value="fa">فارسی</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </div>

            {/* Date Format */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">فرمت تاریخ</p>
                  <p className="text-sm text-gray-400">نمایش تاریخ به صورت شمسی یا میلادی</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className={`btn flex-1 ${settings.dateFormat === 'jalaali' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('dateFormat', 'jalaali')}
                >
                  شمسی
                </button>
                <button
                  className={`btn flex-1 ${settings.dateFormat === 'gregorian' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('dateFormat', 'gregorian')}
                >
                  میلادی
                </button>
              </div>
            </div>

            {/* Currency Format */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">واحد پول</p>
                  <p className="text-sm text-gray-400">نمایش قیمت‌ها به تومان یا ریال</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className={`btn flex-1 ${settings.currencyFormat === 'toman' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('currencyFormat', 'toman')}
                >
                  تومان
                </button>
                <button
                  className={`btn flex-1 ${settings.currencyFormat === 'rial' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('currencyFormat', 'rial')}
                >
                  ریال
                </button>
              </div>
            </div>

            {/* Notifications */}
            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">اعلان‌ها</p>
                  <p className="text-sm text-gray-400">دریافت اعلان‌های سیستم</p>
                </div>
              </div>
              <input 
                type="checkbox" 
                className="toggle toggle-primary" 
                checked={settings.notifications}
                onChange={(e) => updateSetting('notifications', e.target.checked)}
              />
            </div>

            {/* Auto Save */}
            <div className="flex items-center justify-between p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-white">ذخیره خودکار</p>
                  <p className="text-sm text-gray-400">ذخیره خودکار تغییرات</p>
                </div>
              </div>
              <input 
                type="checkbox" 
                className="toggle toggle-primary" 
                checked={settings.autoSave}
                onChange={(e) => updateSetting('autoSave', e.target.checked)}
              />
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          /* ... همان کد قبلی بدون تغییر ... */
          <div className="space-y-6">
            {/* Clear Cache */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white">پاک کردن کش</p>
                    <p className="text-sm text-gray-400">حذف داده‌های موقت برنامه</p>
                  </div>
                </div>
                <button 
                  className={`btn btn-warning btn-outline gap-2 ${clearing ? 'loading' : ''}`}
                  onClick={clearCache}
                  disabled={clearing}
                >
                  {clearing ? 'در حال پاک کردن...' : 'پاک کردن'}
                </button>
              </div>
            </div>

            {/* Export Data */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white">خروجی داده‌ها</p>
                    <p className="text-sm text-gray-400">دانلود تمام داده‌ها به صورت JSON</p>
                  </div>
                </div>
                <button 
                  className={`btn btn-success btn-outline gap-2 ${exporting ? 'loading' : ''}`}
                  onClick={exportData}
                  disabled={exporting}
                >
                  {exporting ? 'در حال خروجی...' : 'دانلود'}
                </button>
              </div>
            </div>

            {/* Import Data */}
            <div className="p-4 bg-base-200/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-info/20 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white">ورود داده‌ها</p>
                    <p className="text-sm text-gray-400">بارگذاری داده‌ها از فایل JSON</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {importError && (
                    <div className="alert alert-error alert-sm py-2">
                      <span>{importError}</span>
                    </div>
                  )}
                  {importSuccess && (
                    <div className="alert alert-success alert-sm py-2">
                      <span>{importSuccess}</span>
                    </div>
                  )}
                  <label className={`btn btn-info btn-outline gap-2 cursor-pointer ${importing ? 'loading' : ''}`}>
                    <input type="file" accept=".json" className="hidden" onChange={importData} disabled={importing} />
                    {importing ? 'در حال بارگذاری...' : 'بارگذاری'}
                  </label>
                </div>
              </div>
            </div>

            {/* Reset Settings */}
            <div className="p-4 bg-error/10 border border-error/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-error/20 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-white">بازنشانی تنظیمات</p>
                    <p className="text-sm text-gray-400">بازگشت به تنظیمات پیش‌فرض</p>
                  </div>
                </div>
                <button className="btn btn-error btn-outline" onClick={resetSettings}>
                  بازنشانی
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-6 gap-3">
        <button className="btn btn-ghost" onClick={resetSettings}>
          لغو تغییرات
        </button>
        <button 
          className={`btn btn-primary gap-2 ${saved ? 'btn-success' : ''}`}
          onClick={saveSettings}
        >
          {saved ? 'ذخیره شد' : 'ذخیره تنظیمات'}
        </button>
      </div>
    </div>
  );
}

export default Setting;